import React, { useState } from "react";
import { Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { Skeleton } from "@progress/kendo-react-indicators";
import { TileLayout } from "@progress/kendo-react-layout";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { toggleFullScreenModeIcon } from "@progress/kendo-svg-icons";
import { useHistory } from "react-router";
import { DateRangePicker } from "@progress/kendo-react-dateinputs";
import { HeaderPage } from "../base/headerPage";
import { startDateInputSettings, endDateInputSettings } from "../filterQuery/filterUtils";
import CustomDropdown from "src/components/customDropdown";
import MultiSelectDropdown from "src/components/multiSelect";
import { CHART_COLOR_PALETTE, CHART_LAYOUT_OPTIONS } from "src/constants/constants";
import { isYearValidate } from "src/utils/utils";
import { transformDateTime } from "src/lib/date-time.util";
import DashboardChart from "./DashboardChart";
import userPositionApi from "./service";

import "./index.scss";

const convertDate = (date) =>
  transformDateTime({
    date,
    format: "YYYY-MM-DD",
  });

const inSourceFilterValue = (data) => {
  if (!Array.isArray(data)) return "";
  const filtered = data.filter((x) => x.text !== "Select All");
  if (filtered.length === 0) return "";
  return filtered.map((item) => `"${item.text}"`).toString();
};

const isBlankEventDataSourceValue = (value) => {
  if (value === null || value === undefined) return true;
  const normalized = String(value).trim();
  return normalized === "" || normalized.toLowerCase() === "blank";
};

const mapEventDataSourceOptions = (rows = []) =>
  (rows || [])
    .filter((item) => !isBlankEventDataSourceValue(item?.event_data_source))
    .map((item) => ({
      text: item.event_data_source,
      id: item.event_data_source,
    }));

const CHART_TITLES = {
  processedVsNewRecords: "Total Records by Processed vs New Records (Rolling 12 Months)",
  eventAndTotalAmountTrend: "Event Amount and Total Amount Trend (Rolling 12 Months)",
  eventAmountTrend: "Event Amount Trend (Rolling 12 Months)",
  eventAmountByDataSource: "Event Amount by Data Source",
  eventAndTotalAmountByDataSource: "Event Amount & Total Amount by Data Source",
  topPositionsByEventAmount: "Top 10 positions by Event Amount",
  loadErrorRecordCount: "Load, Error & Unchanged Records by Last 5 Batches",
  eventAndTotalAmountByBatch: "Event Amount & Total Amount by Last 5 Batches",
};

const CHART_INFO_TOOLTIPS = {
  processedVsNewRecords:
    "Shows monthly New Records (columns) and Processed Records (line) for the selected Event Type.",
  eventAndTotalAmountTrend:
    "Shows Event Amount and Total Amount trends for the rolling last 12 months for the selected Event Type.",
  eventAmountTrend: "Shows Event Amount trend for the rolling last 12 months for the selected Event Type.",
  eventAmountByDataSource:
    "Shows Event Amount and Total Amount by data source for the rolling last 12 months for the selected Event Type and Event Source.",
  eventAndTotalAmountByDataSource:
    "Shows Event Amount and Total Amount by data source for the rolling last 12 months for the selected Event Type and Event Source.",
  topPositionsByEventAmount: "Shows the top 10 positions ranked by Event Amount for the selected Event Type.",
  loadErrorRecordCount:
    "Shows Load, Error, and Unchanged record counts for the last 5 batches for the selected Event Type.",
  eventAndTotalAmountByBatch: "Shows Event Amount and Total Amount for the last 5 batches for the selected Event Type.",
};

const coerceLayoutOption = (option) => {
  if (!option) return CHART_LAYOUT_OPTIONS[1] || CHART_LAYOUT_OPTIONS[0];
  const optionValue = option?.value;
  const allowed = CHART_LAYOUT_OPTIONS.find((item) => item.value === optionValue);
  return allowed || CHART_LAYOUT_OPTIONS[1] || CHART_LAYOUT_OPTIONS[0];
};

const layoutValueForCount = (count) => {
  if (count <= 1) return "flex100";
  if (count === 2) return "flex60";
  return "flex30";
};

const buildRowsFromRule = (chartIds, changedChartId, changedLayoutValue, currentRows) => {
  const all = [...(chartIds || [])].filter(Boolean);
  const safeChangedId = all.includes(changedChartId) ? changedChartId : all[0];

  const findRowIndex = () => {
    if (!currentRows?.length) return 0;
    const idx = currentRows.findIndex((row) => row.includes(safeChangedId));
    return idx >= 0 ? idx : 0;
  };
  const rowIndex = findRowIndex();

  const remaining = all.filter((id) => id !== safeChangedId);

  if (changedLayoutValue === "flex100") {
    const row0 = rowIndex === 0 ? [safeChangedId] : remaining.slice(0, 3);
    const row1 = rowIndex === 0 ? remaining.slice(0, 3) : [safeChangedId];
    return [row0, row1].filter((r) => r.length);
  }

  if (changedLayoutValue === "flex30") {
    const threeRow = rowIndex === 0 ? all.slice(0, 3) : all.slice(Math.max(all.length - 3, 0));
    const oneRow = all.filter((id) => !threeRow.includes(id));
    return rowIndex === 0 ? [threeRow, oneRow] : [oneRow, threeRow];
  }

  return [all.slice(0, 2), all.slice(2, 4)].filter((r) => r.length);
};

const computeTwoRowAutoLayouts = (
  chartIds,
  chartLayouts,
  changedChartId = null,
  changedLayoutValue = null,
  currentRows = null
) => {
  const layoutsSnapshot = { ...(chartLayouts || {}) };
  const ids = [...(chartIds || [])].filter(Boolean);

  let effectiveChangedId = changedChartId;
  let effectiveChangedValue = changedLayoutValue;
  let effectiveRows = currentRows;

  if (!effectiveRows) {
    effectiveRows = [ids.slice(0, 2), ids.slice(2, 4)].filter((r) => r.length);
  }

  if (!effectiveChangedId || !effectiveChangedValue) {
    const flex100Id = ids.find((id) => coerceLayoutOption(layoutsSnapshot[id])?.value === "flex100");
    if (flex100Id) {
      effectiveChangedId = flex100Id;
      effectiveChangedValue = "flex100";
    } else {
      const flex30Id = ids.find((id) => coerceLayoutOption(layoutsSnapshot[id])?.value === "flex30");
      if (flex30Id) {
        effectiveChangedId = flex30Id;
        effectiveChangedValue = "flex30";
      } else {
        effectiveChangedId = ids[0] || null;
        effectiveChangedValue = "flex60";
      }
    }
  }

  const rows = buildRowsFromRule(ids, effectiveChangedId, effectiveChangedValue, effectiveRows);
  const nextLayouts = { ...(chartLayouts || {}) };

  rows.forEach((row) => {
    const value = layoutValueForCount(row.length);
    row.forEach((id) => {
      nextLayouts[id] = coerceLayoutOption({ value });
    });
  });

  if (effectiveChangedId) {
    nextLayouts[effectiveChangedId] = coerceLayoutOption({ value: effectiveChangedValue });
  }

  return { rows, nextLayouts };
};

const CHART_LABEL_MAP = {
  processedVsNewRecords: "event_processed_vs_new_left",
  eventAndTotalAmountTrend: "event_processed_vs_new_right",
  eventAmountTrend: "event_amount_by_source",
  eventAmountByDataSource: "event_total_amount_by_source",
  eventAndTotalAmountByDataSource: "event_amount_total_by_data_source",
  topPositionsByEventAmount: "event_top_10_positions_by_event_amount",
  loadErrorRecordCount: "event_load_error_last5",
  eventAndTotalAmountByBatch: "event_amount_total_last5_batches",
};

const CHART_ID_BY_LABEL = Object.fromEntries(
  Object.entries(CHART_LABEL_MAP).map(([chartId, label]) => [label, chartId])
);

const DEFAULT_CHART_ORDER = Object.keys(CHART_LABEL_MAP);

const normalizeChartOrder = (order = []) => {
  const uniqueOrder = Array.from(new Set(order.filter(Boolean)));
  const baseOrder = [...uniqueOrder, ...DEFAULT_CHART_ORDER.filter((id) => !uniqueOrder.includes(id))];
  const pinnedStart = [
    "processedVsNewRecords",
    "eventAmountTrend",
    "eventAmountByDataSource",
    "topPositionsByEventAmount",
  ];
  const remainingOrder = baseOrder.filter((id) => !pinnedStart.includes(id));
  return [...pinnedStart, ...remainingOrder];
};

const DEFAULT_CHART_LAYOUTS = DEFAULT_CHART_ORDER.reduce((acc, chartId) => {
  acc[chartId] = CHART_LAYOUT_OPTIONS.find((opt) => opt.value === "flex60") || CHART_LAYOUT_OPTIONS[0];
  return acc;
}, {});
const EVENT_DASHBOARD_OBJECT_TYPE = 8;
const RIGHT_PANEL_CHART_IDS = ["loadErrorRecordCount", "eventAndTotalAmountByBatch"];

const getLayoutOption = (layoutValue) => {
  if (layoutValue === null || layoutValue === undefined || layoutValue === "") {
    return CHART_LAYOUT_OPTIONS[0];
  }

  const normalizedLayoutValue = String(layoutValue).trim().toLowerCase();
  const byValue = CHART_LAYOUT_OPTIONS.find((option) => String(option.value).toLowerCase() === normalizedLayoutValue);
  if (byValue) return byValue;

  const byText = CHART_LAYOUT_OPTIONS.find((option) => String(option.text).toLowerCase() === normalizedLayoutValue);
  if (byText) return byText;

  const numericIndex = Number(layoutValue);
  if (!Number.isNaN(numericIndex) && numericIndex > 0 && numericIndex <= CHART_LAYOUT_OPTIONS.length) {
    return CHART_LAYOUT_OPTIONS[numericIndex - 1];
  }

  return CHART_LAYOUT_OPTIONS[0];
};

const getApiLayoutValue = (layoutOption) => {
  if (!layoutOption) return null;
  const optionIndex = CHART_LAYOUT_OPTIONS.findIndex((option) => option.value === layoutOption.value);
  if (optionIndex < 0) return null;
  return optionIndex + 1;
};

const formatEventRecordCount = (value) => {
  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return new Intl.NumberFormat("en-US", {
      notation: numericValue >= 1000 ? "compact" : "standard",
      maximumFractionDigits: 1,
    }).format(numericValue);
  }

  return value || "0";
};

const formatExactRecordCount = (value) => {
  const numericValue = Number(value);

  if (Number.isFinite(numericValue)) {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
    }).format(numericValue);
  }

  return value || "0";
};

const EMPTY_EVENT_DASHBOARD_CARD_METRICS = {
  total_records: 0,
  new_records: 0,
  processed_records: 0,
  unassigned_records: 0,
};

const formatAmountValue = (value) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(toNumeric(value));
};

const formatAmountValueInK = (value) => {
  const numericValue = toNumeric(value);
  const absValue = Math.abs(numericValue);
  const compactFormat = (divisor, suffix) =>
    `${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(numericValue / divisor)}${suffix}`;

  if (absValue >= 1_000_000_000) return compactFormat(1_000_000_000, "B");
  if (absValue >= 1_000_000) return compactFormat(1_000_000, "M");
  if (absValue >= 1000) return compactFormat(1000, "K");
  return formatAmountValue(numericValue);
};

const formatPercentValue = (value) => {
  const numericValue = toNumeric(value);
  return `${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(numericValue)}%`;
};

const formatBatchAxisLabel = (value) => {
  const text = String(value || "");
  if (text.length <= 10) return text;
  return text.match(/.{1,10}/g)?.join("\n") || text;
};

const formatMonthAxisLabel = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const monYearMatch = text.match(/^([A-Za-z]{3})[-_/\s](\d{4})$/);
  if (monYearMatch) {
    const month = `${monYearMatch[1].charAt(0).toUpperCase()}${monYearMatch[1].slice(1).toLowerCase()}`;
    return `${month}'${monYearMatch[2].slice(-2)}`;
  }

  const compactMatch = text.match(/^([A-Za-z]{3})'(\d{2})$/);
  if (compactMatch) {
    const month = `${compactMatch[1].charAt(0).toUpperCase()}${compactMatch[1].slice(1).toLowerCase()}`;
    return `${month}'${compactMatch[2]}`;
  }

  return text;
};

const toNumeric = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sumSeriesDataByName = (series, name) => {
  const s = (series || []).find((item) => item.name === name);
  if (!s || !Array.isArray(s.data)) return 0;
  return s.data.reduce((acc, v) => acc + toNumeric(v), 0);
};

const getRowValueByKeyAliases = (row, aliases) => {
  if (!row || typeof row !== "object") return undefined;
  const normalizedAliasSet = new Set(
    aliases.map((alias) =>
      String(alias)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "")
    )
  );

  const rowKey = Object.keys(row).find((key) =>
    normalizedAliasSet.has(
      String(key)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "")
    )
  );

  return rowKey ? row[rowKey] : undefined;
};

const getCompactDate = (value) => {
  const dateText = String(value || "").trim();
  if (!dateText) return "";
  const datePart = dateText.split(" ")[0] || "";
  return datePart.replace(/-/g, "");
};

const getBatchCategoryLabel = (row) => {
  const batchName = getRowValueByKeyAliases(row, ["etl_batch_name", "batch_name", "batch"]);
  const batchStartDate = getRowValueByKeyAliases(row, ["etl_start_datetime", "start_datetime", "batch_date"]);

  if (batchName) {
    const compactDate = getCompactDate(batchStartDate);
    return compactDate ? `${batchName}_${compactDate}` : String(batchName);
  }

  return row.category_axis ?? row.group_period_name ?? row.category ?? row.x_axis ?? "";
};

const getShortBatchDate = (value) => {
  const dateText = String(value || "").trim();
  if (!dateText) return "";
  const datePart = dateText.split(" ")[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return datePart;
  return `${month}/${day}/${year}`;
};

const getBatchAxisCategory = (row) => {
  const batchStartDate = getRowValueByKeyAliases(row, ["etl_start_datetime", "start_datetime", "batch_date"]);
  const shortDate = getShortBatchDate(batchStartDate);

  if (shortDate) return shortDate;
  return getBatchCategoryLabel(row);
};

const getBatchTooltipName = (row) =>
  String(getRowValueByKeyAliases(row, ["etl_batch_name", "batch_name", "batch"]) || "");

const makeUniqueBatchCategories = (rawLabels) => {
  const seen = new Map();
  return rawLabels.map((label) => {
    const count = seen.get(label) || 0;
    seen.set(label, count + 1);
    return count === 0 ? label : `${label}${"\u200b".repeat(count)}`;
  });
};

const formatBatchCategoryLabel = (value) => String(value || "");

const isNullLikeDashboardEventTypeKey = (value) => {
  if (value === null || value === undefined) return true;
  const normalized = String(value).trim().toLowerCase();
  return normalized === "" || normalized === "null" || normalized === "undefined" || normalized === "0";
};

const normalizeBatchAmountChartData = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      categories: [],
      series: [],
    };
  }

  const firstRow = rows[0] || {};
  const hasSeriesShape = firstRow.series_name !== undefined || firstRow.series_data !== undefined;

  const categoryFromRow = (row) => getBatchCategoryLabel(row);

  if (hasSeriesShape) {
    const categories = [...new Set(rows.map((row) => String(categoryFromRow(row) || "")))].filter(Boolean);
    const hasSeriesByKeys = (keys) =>
      rows.some((item) => {
        const seriesName = String(item.series_name || "").toLowerCase();
        return keys.some((key) => seriesName.includes(key));
      });

    const eventAmountData = categories.map((category) => {
      const row = rows.find((item) => {
        const seriesName = String(item.series_name || "").toLowerCase();
        return (
          String(categoryFromRow(item) || "") === category &&
          (seriesName.includes("event amt") || seriesName.includes("event amount") || seriesName === "event")
        );
      });
      return toNumeric(row?.series_data);
    });

    const totalAmountData = categories.map((category) => {
      const row = rows.find((item) => {
        const seriesName = String(item.series_name || "").toLowerCase();
        return (
          String(categoryFromRow(item) || "") === category &&
          (seriesName.includes("total amt") || seriesName.includes("total amount") || seriesName === "total")
        );
      });
      return toNumeric(row?.series_data);
    });

    const series = [];
    if (hasSeriesByKeys(["event amt", "event amount", "event"])) {
      series.push({
        name: "Event Amount",
        data: eventAmountData,
        color: CHART_COLOR_PALETTE[0],
        // type: "area",
        type: "column",
        axis: "leftAxis",
      });
    }
    if (hasSeriesByKeys(["total amt", "total amount", "total"])) {
      series.push({
        name: "Total Amount",
        data: totalAmountData,
        color: CHART_COLOR_PALETTE[1],
        type: "line",
        axis: "rightAxis",
      });
    }

    return { categories, series };
  }

  const rawCategories = rows.map((row, index) => {
    const label = String(getBatchAxisCategory(row) || "").trim();
    return label || `Batch ${index + 1}`;
  });
  const categories = makeUniqueBatchCategories(rawCategories);
  const categoryTooltips = {};
  categories.forEach((category, index) => {
    categoryTooltips[category] = getBatchTooltipName(rows[index]);
  });
  const eventAmountData = rows.map((row) => toNumeric(row.event_amount ?? row.event_amt ?? row.amount ?? 0));
  const totalAmountData = rows.map((row) =>
    toNumeric(row.total_amount ?? row.total_amt ?? row.total ?? row.total_series_data ?? 0)
  );
  const hasField = (aliases) => rows.some((row) => getRowValueByKeyAliases(row, aliases) !== undefined);
  const series = [];
  if (hasField(["event_amount", "event_amt", "amount"])) {
    series.push({
      name: "Event Amount",
      data: eventAmountData,
      color: CHART_COLOR_PALETTE[0],
      type: "column",
      axis: "leftAxis",
    });
  }
  if (hasField(["total_amount", "total_amt", "total", "total_series_data"])) {
    series.push({
      name: "Total Amount",
      data: totalAmountData,
      color: CHART_COLOR_PALETTE[1],
      type: "line",
      axis: "rightAxis",
    });
  }

  return { categories, series, categoryTooltips };
};

const getBatchCountValue = (row, fieldAliases) => toNumeric(getRowValueByKeyAliases(row, fieldAliases));

const getUnchangedBatchCountValue = (row) => getBatchCountValue(row, ["unchanged_rec_count", "unchangedreccount"]);

const normalizeBatchStatusChartData = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      categories: [],
      series: [],
    };
  }

  const firstRow = rows[0] || {};
  const hasSeriesShape = firstRow.series_name !== undefined || firstRow.series_data !== undefined;
  const categoryFromRow = (row) => getBatchCategoryLabel(row);

  if (hasSeriesShape) {
    const categories = [...new Set(rows.map((row) => String(categoryFromRow(row) || "")))].filter(Boolean);
    const hasSeriesByKeys = (keys) =>
      rows.some((item) => {
        const seriesName = String(item.series_name || "").toLowerCase();
        return keys.some((key) => seriesName.includes(key));
      });
    const valueForSeries = (category, keys) => {
      const row = rows.find((item) => {
        const seriesName = String(item.series_name || "").toLowerCase();
        return String(categoryFromRow(item) || "") === category && keys.some((key) => seriesName.includes(key));
      });
      return toNumeric(row?.series_data);
    };
    const unchangedRecData = categories.map((category) => valueForSeries(category, ["unchanged"]));
    const totalRecData = categories.map((category) => valueForSeries(category, ["total"]));
    const errorRecData = categories.map((category) => valueForSeries(category, ["error"]));
    const loadRecData = categories.map((category) => valueForSeries(category, ["load"]));

    const series = [];
    if (hasSeriesByKeys(["load"])) {
      series.push({
        name: "Load Records",
        data: loadRecData,
        color: "#fa8c16",
        type: "column",
        stack: true,
        axis: "leftAxis",
      });
    }
    if (hasSeriesByKeys(["error"])) {
      series.push({
        name: "Error Records",
        data: errorRecData,
        color: "#ff4d4f",
        type: "column",
        stack: true,
        axis: "leftAxis",
      });
    }
    if (hasSeriesByKeys(["unchanged"])) {
      series.push({
        name: "Unchanged Records",
        data: unchangedRecData,
        color: CHART_COLOR_PALETTE[0],
        type: "column",
        stack: true,
        axis: "leftAxis",
      });
    }
    if (hasSeriesByKeys(["total"])) {
      series.push({
        name: "Total Records",
        data: totalRecData,
        color: CHART_COLOR_PALETTE[1],
        type: "line",
        axis: "rightAxis",
      });
    }

    return { categories, series };
  }

  const rawCategories = rows.map((row, index) => {
    const label = String(getBatchAxisCategory(row) || "").trim();
    return label || `Batch ${index + 1}`;
  });
  const categories = makeUniqueBatchCategories(rawCategories);
  const categoryTooltips = {};
  categories.forEach((category, index) => {
    categoryTooltips[category] = getBatchTooltipName(rows[index]);
  });
  const unchangedRecData = rows.map((row) => getUnchangedBatchCountValue(row));
  const totalRecData = rows.map((row) => getBatchCountValue(row, ["total_rec_count", "totalreccount"]));
  const errorRecData = rows.map((row) => getBatchCountValue(row, ["error_rec_count", "errorreccount"]));
  const loadRecData = rows.map((row) => getBatchCountValue(row, ["load_rec_count", "loadreccount"]));

  const hasField = (aliases) => rows.some((row) => getRowValueByKeyAliases(row, aliases) !== undefined);
  const series = [];
  if (hasField(["load_rec_count", "loadreccount"]) || loadRecData.some((value) => Number.isFinite(value))) {
    series.push({
      name: "Load Records",
      data: loadRecData,
      color: "#fa8c16",
      type: "column",
      stack: true,
      axis: "leftAxis",
    });
  }
  if (hasField(["error_rec_count", "errorreccount"]) || errorRecData.some((value) => Number.isFinite(value))) {
    series.push({
      name: "Error Records",
      data: errorRecData,
      color: "#ff4d4f",
      type: "column",
      stack: true,
      axis: "leftAxis",
    });
  }
  if (
    hasField(["unchanged_rec_count", "unchangedreccount"]) ||
    unchangedRecData.some((value) => Number.isFinite(value))
  ) {
    series.push({
      name: "Unchanged Records",
      data: unchangedRecData,
      color: CHART_COLOR_PALETTE[0],
      type: "column",
      stack: true,
      axis: "leftAxis",
    });
  }
  if (hasField(["total_rec_count", "totalreccount"]) || totalRecData.some((value) => Number.isFinite(value))) {
    series.push({
      name: "Total Records",
      data: totalRecData,
      color: CHART_COLOR_PALETTE[1],
      type: "line",
      axis: "rightAxis",
    });
  }

  return { categories, series, categoryTooltips };
};

const normalizeProcessedVsNewChartData = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      categories: [],
      series: [],
    };
  }

  const categories = rows
    .map((row) =>
      String(getRowValueByKeyAliases(row, ["month", "category_axis", "group_period_name", "period_name"]) || "").trim()
    )
    .filter(Boolean);
  const hasField = (aliases) => rows.some((row) => getRowValueByKeyAliases(row, aliases) !== undefined);
  const newRecordsData = rows.map((row) => toNumeric(getRowValueByKeyAliases(row, ["new_records", "newrecords"])));
  const processedRecordsData = rows.map((row) =>
    toNumeric(getRowValueByKeyAliases(row, ["processed_records", "processedrecords"]))
  );

  const series = [];
  if (hasField(["new_records", "newrecords"])) {
    series.push({
      name: "New Records",
      data: newRecordsData,
      color: CHART_COLOR_PALETTE[0],
      type: "column",
      axis: "leftAxis",
    });
  }
  if (hasField(["processed_records", "processedrecords"])) {
    series.push({
      name: "Processed Records",
      data: processedRecordsData,
      color: CHART_COLOR_PALETTE[1],
      type: "line",
      axis: "rightAxis",
    });
  }

  return { categories, series };
};

const normalizeEventAndTotalAmountTrendRolling12MonthsData = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      categories: [],
      series: [],
    };
  }

  const categoryFromRow = (row) =>
    String(
      getRowValueByKeyAliases(row, [
        "rolling_12_months",
        "rolling_12_month",
        "rolling_month",
        "month",
        "month_name",
        "category_axis",
        "group_period_name",
        "period_name",
        "x_axis",
      ]) || ""
    ).trim();

  const firstRow = rows[0] || {};
  const hasSeriesShape = firstRow.series_name !== undefined || firstRow.series_data !== undefined;

  if (hasSeriesShape) {
    const categories = [...new Set(rows.map((row) => categoryFromRow(row)))].filter(Boolean);
    const hasSeriesByKeys = (keys) =>
      rows.some((item) => {
        const seriesName = String(item.series_name || "").toLowerCase();
        return keys.some((key) => seriesName.includes(key));
      });
    const valueForSeries = (category, keys) => {
      const row = rows.find((item) => {
        const seriesName = String(item.series_name || "").toLowerCase();
        return String(categoryFromRow(item) || "") === category && keys.some((key) => seriesName.includes(key));
      });
      return toNumeric(row?.series_data);
    };

    const eventAmountData = categories.map((category) =>
      valueForSeries(category, ["event amount", "event amt", "event"])
    );
    const totalAmountData = categories.map((category) =>
      valueForSeries(category, ["total amount", "total amt", "total"])
    );

    const series = [];
    if (hasSeriesByKeys(["event amount", "event amt", "event"])) {
      series.push({
        name: "Event Amount",
        data: eventAmountData,
        color: CHART_COLOR_PALETTE[0],
        type: "line",
        axis: "leftAxis",
      });
    }
    if (hasSeriesByKeys(["total amount", "total amt", "total"])) {
      series.push({
        name: "Total Amount",
        data: totalAmountData,
        color: CHART_COLOR_PALETTE[1],
        type: "line",
        axis: "rightAxis",
      });
    }

    return { categories, series };
  }

  const categories = rows.map((row) => categoryFromRow(row)).filter(Boolean);
  const eventAmountData = rows.map((row) =>
    toNumeric(getRowValueByKeyAliases(row, ["event_amount", "event_amt", "eventamount", "amount"]))
  );
  const totalAmountData = rows.map((row) =>
    toNumeric(getRowValueByKeyAliases(row, ["total_amount", "total_amt", "totalamount", "total"]))
  );

  const hasField = (aliases) => rows.some((row) => getRowValueByKeyAliases(row, aliases) !== undefined);
  const series = [];
  if (hasField(["event_amount", "event_amt", "eventamount", "amount"])) {
    series.push({
      name: "Event Amount",
      data: eventAmountData,
      color: CHART_COLOR_PALETTE[0],
      type: "line",
      axis: "leftAxis",
    });
  }
  if (hasField(["total_amount", "total_amt", "totalamount", "total"])) {
    series.push({
      name: "Total Amount",
      data: totalAmountData,
      color: CHART_COLOR_PALETTE[1],
      type: "line",
      axis: "rightAxis",
    });
  }

  return { categories, series };
};

const normalizeAmountByDataSourceLineData = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      categories: [],
      series: [],
    };
  }

  const categoryAliases = [
    "month",
    "event_data_source",
    "data_source",
    "source",
    "category_axis",
    "group_name",
    "label",
    "name",
  ];
  const categoryFromRow = (row) => String(getRowValueByKeyAliases(row, categoryAliases) || "").trim();
  const firstRow = rows[0] || {};
  const hasSeriesShape = firstRow.series_name !== undefined || firstRow.series_data !== undefined;

  if (hasSeriesShape) {
    const categories = [...new Set(rows.map((row) => categoryFromRow(row)))].filter(Boolean);
    const seriesNames = [...new Set(rows.map((row) => String(row.series_name || "").trim()).filter(Boolean))];

    const pickColor = (name) => {
      const normalized = String(name || "").toLowerCase();
      if (normalized.includes("event") && normalized.includes("%")) return CHART_COLOR_PALETTE[2] || "#7c3aed";
      if (normalized.includes("total") && normalized.includes("%")) return CHART_COLOR_PALETTE[3] || "#f59e0b";
      if (normalized.includes("total")) return CHART_COLOR_PALETTE[1] || "#16a34a";
      if (normalized.includes("event")) return CHART_COLOR_PALETTE[0] || "#2563eb";
      return CHART_COLOR_PALETTE[0] || "#2563eb";
    };

    const series = seriesNames.map((seriesName) => ({
      name: seriesName,
      type: "line",
      color: pickColor(seriesName),
      axis: String(seriesName).includes("%") ? "rightAxis" : "leftAxis",
      data: categories.map((category) => {
        const matchedRow = rows.find(
          (row) => categoryFromRow(row) === category && String(row.series_name || "").trim() === seriesName
        );
        return toNumeric(matchedRow?.series_data);
      }),
    }));

    return { categories, series };
  }

  const preparedData = rows
    .map((row) => ({
      category: categoryFromRow(row),
      eventAmount: toNumeric(getRowValueByKeyAliases(row, ["event_amount", "event_amt", "eventamount", "amount"])),
      totalAmount: toNumeric(getRowValueByKeyAliases(row, ["total_amount", "total_amt", "totalamount", "total"])),
      eventAmountPercent: toNumeric(getRowValueByKeyAliases(row, ["event_amount_percent", "eventamountpercent"])),
      totalAmountPercent: toNumeric(getRowValueByKeyAliases(row, ["total_amount_percent", "totalamountpercent"])),
    }))
    .filter((item) => item.category);
  const hasField = (aliases) => rows.some((row) => getRowValueByKeyAliases(row, aliases) !== undefined);

  const series = [];
  if (hasField(["event_amount", "event_amt", "eventamount", "amount"])) {
    series.push({
      name: "Event Amount",
      type: "line",
      color: CHART_COLOR_PALETTE[0],
      axis: "leftAxis",
      data: preparedData.map((item) => item.eventAmount),
    });
  }
  if (hasField(["total_amount", "total_amt", "totalamount", "total"])) {
    series.push({
      name: "Total Amount",
      type: "line",
      color: CHART_COLOR_PALETTE[1],
      axis: "leftAxis",
      data: preparedData.map((item) => item.totalAmount),
    });
  }
  if (hasField(["event_amount_percent", "eventamountpercent"])) {
    series.push({
      name: "Event Amount %",
      type: "line",
      color: CHART_COLOR_PALETTE[2] || "#7c3aed",
      axis: "rightAxis",
      data: preparedData.map((item) => item.eventAmountPercent),
    });
  }
  if (hasField(["total_amount_percent", "totalamountpercent"])) {
    series.push({
      name: "Total Amount %",
      type: "line",
      color: CHART_COLOR_PALETTE[3] || "#f59e0b",
      axis: "rightAxis",
      data: preparedData.map((item) => item.totalAmountPercent),
    });
  }

  return { categories: preparedData.map((item) => item.category), series };
};

const normalizeTopPositionsByEventAmountData = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      categories: [],
      series: [],
    };
  }

  const amountByPosition = rows.reduce((acc, row) => {
    const category = String(
      getRowValueByKeyAliases(row, ["position_name", "position", "category_axis", "label", "name"]) || ""
    ).trim();
    if (!category) return acc;

    const eventAmount = toNumeric(getRowValueByKeyAliases(row, ["event_amount", "event_amt", "eventamount", "amount"]));
    acc.set(category, (acc.get(category) || 0) + eventAmount);
    return acc;
  }, new Map());

  const preparedData = [...amountByPosition.entries()]
    .map(([category, eventAmount]) => ({ category, eventAmount }))
    .sort((a, b) => b.eventAmount - a.eventAmount)
    .slice(0, 10);

  return {
    categories: preparedData.map((item) => item.category),
    series: [
      {
        name: "Event Amount",
        data: preparedData.map((item) => item.eventAmount),
        color: CHART_COLOR_PALETTE[0],
        type: "bar",
      },
    ],
  };
};

const CHART_RENDER_CONFIG = {
  processedVsNewRecords: {
    apiMethod: "getEventDashboardProcessedVsNewRecordsData",
    normalize: normalizeProcessedVsNewChartData,
    valueFormatter: formatEventRecordCount,
    secondaryValueFormatter: formatEventRecordCount,
    tooltipValueFormatter: formatExactRecordCount,
    tooltipShowCategory: false,
    categoryFormatter: formatMonthAxisLabel,
    labelRotation: -25,
    useDualAxis: true,
    syncDualAxisScale: false,
    leftAxisTitle: "New Records",
    rightAxisTitle: "Processed Records",
    categoryAxisTitle: "Month",
    getPayload: (eventTypeId) => ({
      in_event_type_mstr_key: eventTypeId,
    }),
  },
  eventAndTotalAmountTrend: {
    apiMethod: "getEventDashboardEventTotalAmountTrendRollingData",
    normalize: normalizeEventAndTotalAmountTrendRolling12MonthsData,
    valueFormatter: formatAmountValueInK,
    secondaryValueFormatter: formatAmountValueInK,
    tooltipValueFormatter: formatAmountValue,
    tooltipShowCategory: false,
    categoryFormatter: formatMonthAxisLabel,
    labelRotation: -25,
    useDualAxis: true,
    leftAxisTitle: "Event Amount",
    rightAxisTitle: "Total Amount",
    categoryAxisTitle: "Month",
    getPayload: (eventTypeId) => ({
      in_event_type_mstr_key: eventTypeId,
    }),
  },
  eventAmountTrend: {
    apiMethod: "getEventDashboardAmountByDataSourceTrendData",
    normalize: normalizeEventAndTotalAmountTrendRolling12MonthsData,
    valueFormatter: formatAmountValueInK,
    tooltipValueFormatter: formatAmountValue,
    tooltipShowCategory: false,
    categoryFormatter: formatMonthAxisLabel,
    labelRotation: -25,
    leftAxisTitle: "Event Amount",
    categoryAxisTitle: "Month",
    getPayload: (eventTypeId) => ({
      in_event_type_mstr_key: eventTypeId,
    }),
  },
  eventAmountByDataSource: {
    apiMethod: "getEventDashboardTotalAmountByDataSourceTrendData",
    normalize: normalizeAmountByDataSourceLineData,
    valueFormatter: formatAmountValueInK,
    useDualAxis: false,
    tooltipValueFormatter: formatAmountValue,
    tooltipShowCategory: false,
    categoryFormatter: formatMonthAxisLabel,
    labelRotation: -25,
    isPieChart: false,
    requiresEventSource: true,
    leftAxisTitle: "Amount",
    categoryAxisTitle: "Month",
    getPayload: (eventTypeId, eventSourceId) => ({
      in_event_type_mstr_key: eventTypeId,
      in_data_source: eventSourceId || "",
    }),
  },
  eventAndTotalAmountByDataSource: {
    apiMethod: "getEventDashboardAmountAndTotalAmountByDataSourceTrendData",
    normalize: normalizeAmountByDataSourceLineData,
    valueFormatter: formatAmountValueInK,
    secondaryValueFormatter: formatPercentValue,
    useDualAxis: true,
    tooltipValueFormatter: formatAmountValue,
    tooltipShowCategory: false,
    categoryFormatter: formatMonthAxisLabel,
    labelRotation: -25,
    isPieChart: false,
    requiresEventSource: true,
    leftAxisTitle: "Amount",
    rightAxisTitle: "% of Total",
    categoryAxisTitle: "Month",
    getPayload: (eventTypeId, eventSourceId) => ({
      in_event_type_mstr_key: eventTypeId,
      in_data_source: eventSourceId || "",
    }),
  },
  topPositionsByEventAmount: {
    apiMethod: "getEventDashboardTopPositionsByEventAmountData",
    normalize: normalizeTopPositionsByEventAmountData,
    valueFormatter: formatAmountValueInK,
    tooltipValueFormatter: formatAmountValue,
    tooltipShowCategory: false,
    categoryFormatter: (value) => String(value || ""),
    labelRotation: 0,
    valueLabelRotation: -35,
    invertCategoryOrder: false,
    horizontalBarChart: true,
    showLegend: false,
    leftAxisTitle: "Event Amount",
    categoryAxisTitle: "Position",
    getPayload: (eventTypeId) => ({
      in_event_type_mstr_key: eventTypeId,
    }),
  },
  eventAndTotalAmountByBatch: {
    apiMethod: "getEventDashboardAmountAndTotalAmountLastFiveBatchesData",
    normalize: normalizeBatchAmountChartData,
    valueFormatter: formatAmountValueInK,
    secondaryValueFormatter: formatAmountValueInK,
    tooltipValueFormatter: formatAmountValue,
    tooltipShowCategory: false,
    categoryFormatter: formatBatchCategoryLabel,
    labelRotation: -25,
    useDualAxis: true,
    syncDualAxisScale: false,
    leftAxisTitle: "Event Amount",
    rightAxisTitle: "Total Amount",
    categoryAxisTitle: "Batches",
    barGap: 1.2,
    barSpacing: 0.3,
    getPayload: (eventTypeId) => ({
      in_event_type_mstr_key: eventTypeId,
      in_filter: "ALL",
    }),
  },
  loadErrorRecordCount: {
    apiMethod: "getEventDashboardTotalLoadAndErrorCountData",
    normalize: normalizeBatchStatusChartData,
    valueFormatter: formatEventRecordCount,
    secondaryValueFormatter: formatEventRecordCount,
    tooltipValueFormatter: formatExactRecordCount,
    tooltipShowCategory: false,
    categoryFormatter: formatBatchCategoryLabel,
    labelRotation: -25,
    useDualAxis: true,
    syncDualAxisScale: true,
    leftAxisTitle: "Records",
    rightAxisTitle: "Total Records",
    categoryAxisTitle: "Batches",
    barGap: 3,
    barSpacing: 0.6,
    getPayload: (eventTypeId) => ({
      in_event_type_mstr_key: eventTypeId,
      in_filter: "ALL",
    }),
  },
};

const EMPTY_CHART_DATA = {
  categories: [],
  series: [],
};

const SUMMARY_TILE_LAYOUT_ITEM_OPTIONS = {
  reorderable: false,
  resizable: false,
};

const ICONS = {
  records: (
    <svg width="22" height="22" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M19.375 2.5H10.75C10.25 2.5 9.75 2.75 9.375 3.125C9 3.5 8.75 4 8.75 4.5V20.5C8.75 21 9 21.5 9.375 21.875C9.75 22.25 10.25 22.5 10.75 22.5H23C23.5 22.5 24 22.25 24.375 21.875C24.75 21.5 25 21 25 20.5V8.125L19.375 2.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.75 9.5V25.5C3.75 26 4 26.5 4.375 26.875C4.75 27.25 5.25 27.5 5.75 27.5H18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M18.75 2.5V8.75H25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  unassigned: (
    <svg width="22" height="22" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_unassigned)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M19.6406 10.3594C19.2741 9.9938 18.6806 9.9938 18.315 10.3594L15 13.6781L11.685 10.3594C11.3194 9.9938 10.7259 9.9938 10.3594 10.3594C9.99375 10.725 9.99375 11.3156 10.3594 11.6812L13.6744 15L10.3594 18.3188C9.99375 18.6844 9.99375 19.275 10.3594 19.6406C10.7259 20.0062 11.3194 20.0062 11.685 19.6406L15 16.3219L18.315 19.6406C18.6806 20.0062 19.2741 20.0062 19.6406 19.6406C20.0062 19.275 20.0062 18.6844 19.6406 18.3188L16.3256 15L19.6406 11.6812C20.0062 11.3156 20.0062 10.725 19.6406 10.3594ZM28.125 26.25C28.125 27.2812 27.285 28.125 26.25 28.125H3.75C2.715 28.125 1.875 27.2812 1.875 26.25V3.75C1.875 2.71875 2.715 1.875 3.75 1.875H26.25C27.285 1.875 28.125 2.71875 28.125 3.75V26.25ZM26.25 0H3.75C1.67906 0 0 1.67812 0 3.75V26.25C0 28.3219 1.67906 30 3.75 30H26.25C28.3209 30 30 28.3219 30 26.25V3.75C30 1.67812 28.3209 0 26.25 0Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_unassigned">
          <rect width="30" height="30" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),
  new: (
    <svg width="22" height="22" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_new)">
        <path
          d="M24.375 24.375H5.625V5.625H15V3.75H5.625C5.12787 3.7505 4.65125 3.9482 4.29972 4.29972C3.9482 4.65125 3.7505 5.12787 3.75 5.625V24.375C3.7505 24.8721 3.9482 25.3488 4.29972 25.7003C4.65125 26.0518 5.12787 26.2495 5.625 26.25H24.375C24.8721 26.2495 25.3488 26.0518 25.7003 25.7003C26.0518 25.3488 26.2495 24.8721 26.25 24.375V15H24.375V24.375Z"
          fill="currentColor"
        />
        <path
          d="M24.375 5.625V1.875H22.5V5.625H18.75V7.5H22.5V11.25H24.375V7.5H28.125V5.625H24.375Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id="clip0_new">
          <rect width="30" height="30" fill="white" />
        </clipPath>
      </defs>
    </svg>
  ),
  processed: (
    <svg width="22" height="22" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.5 1.25C12.1685 1.25 11.8505 1.3817 11.6161 1.61611L4.11611 9.11611C3.8817 9.35054 3.75 9.66848 3.75 10V25C3.75 27.0711 5.42894 28.75 7.5 28.75H22.5C24.5711 28.75 26.25 27.0711 26.25 25V5C26.25 2.92894 24.5711 1.25 22.5 1.25H12.5ZM13.75 3.75H22.5C23.1904 3.75 23.75 4.30965 23.75 5V25C23.75 25.6904 23.1904 26.25 22.5 26.25H7.5C6.80965 26.25 6.25 25.6904 6.25 25V11.25H12.5C13.1904 11.25 13.75 10.6903 13.75 10V3.75ZM11.25 8.75H8.01776L11.25 5.51776V8.75ZM20.9603 15.8002C21.4023 15.2699 21.3306 14.4816 20.8002 14.0398C20.2699 13.5977 19.4816 13.6694 19.0398 14.1998L13.666 20.6483L10.8839 17.8661C10.3957 17.378 9.60428 17.378 9.11611 17.8661C8.62796 18.3542 8.62796 19.1458 9.11611 19.6339L12.8661 23.3839C13.1145 23.6322 13.4557 23.7646 13.8066 23.7488C14.1575 23.7327 14.4855 23.57 14.7102 23.3002L20.9603 15.8002Z"
        fill="currentColor"
      />
    </svg>
  ),
};

const TILE_KEY_TO_ICON = {
  totalRecords: "records",
  processedRecords: "processed",
  newRecords: "new",
  unassignedRecords: "unassigned",
};

const EventDashSummaryTileIcon = ({ type }) => ICONS[TILE_KEY_TO_ICON[type]] ?? ICONS.records;

const EventDashboard = (pageProps) => {
  const [list, Action] = userPositionApi();
  const history = useHistory();

  const [showRecentSearch, setShowRecentSearch] = React.useState(false);
  const [eventTypeList, setEventTypeList] = React.useState([]);
  const [allEventTypeList, setAllEventTypeList] = React.useState([]);
  // Displayed (search-filtered) list for the top-filter Event Type dropdown. Kept separate
  // from allEventTypeList so typing in the dropdown filters the shown items without losing
  // the full source list used for filtering/reset.
  const [filterEventTypeDisplayList, setFilterEventTypeDisplayList] = React.useState([]);
  const [eventDataSourceList, setEventDataSourceList] = React.useState([]);
  const [filteredEventDataSourceList, setFilteredEventDataSourceList] = React.useState([]);

  const [isEventTypeLoading, setIsEventTypeLoading] = React.useState(true);
  const [isEventSourceLoading, setIsEventSourceLoading] = React.useState(false);

  const [filters, setFilters] = React.useState({
    eventTypeValue: null,
    filterEventTypeValue: null,
    eventSourceValue: null,
  });

  const [dashboardDateFields, setDashboardDateFields] = React.useState({
    in_period_start_date: "",
    in_period_end_date: "",
  });
  const [dashboardDateErrors, setDashboardDateErrors] = React.useState({
    in_period_start_date: "",
    in_period_end_date: "",
  });
  const [dashboardEventDataSourceFields, setDashboardEventDataSourceFields] = React.useState({
    eventDataSourceValue: [],
    eventDataSourceData: [],
    eventDataSourceDataList: [],
    isEventDataSourceDataLoading: false,
  });
  const [dashboardServiceLocationFields, setDashboardServiceLocationFields] = React.useState({
    serviceLocationValue: [],
    serviceLocationData: [],
    serviceLocationDataList: [],
    isServiceLocationDataLoading: false,
  });
  const [dashboardPositionFields, setDashboardPositionFields] = React.useState({
    positionValue: [],
    positionData: [],
    positionDataList: [],
    isPositionDataLoading: false,
  });
  const [dashboardCompPlanFields, setDashboardCompPlanFields] = React.useState({
    complanValue: [],
    complanValueData: [],
    compPlanDataList: [],
    isComPlanValueDataLoading: false,
  });
  const [expandedChartKey, setExpandedChartKey] = React.useState("");
  const [baseChartOrder, setBaseChartOrder] = React.useState(normalizeChartOrder(DEFAULT_CHART_ORDER));
  const [chartOrder, setChartOrder] = React.useState(normalizeChartOrder(DEFAULT_CHART_ORDER));
  const [chartLayouts, setChartLayouts] = React.useState(DEFAULT_CHART_LAYOUTS);
  const [landingPodEventTypeKeyByChartId, setLandingPodEventTypeKeyByChartId] = React.useState({});
  const [eventDashboardCardMetrics, setEventDashboardCardMetrics] = React.useState(null);
  const [isEventCardLoading, setIsEventCardLoading] = React.useState(true);
  const [chartStateById, setChartStateById] = React.useState({
    processedVsNewRecords: {
      loading: true,
      noData: false,
      data: EMPTY_CHART_DATA,
      visibility: {},
    },
    eventAndTotalAmountTrend: {
      loading: true,
      noData: false,
      data: EMPTY_CHART_DATA,
      visibility: {},
    },
    eventAmountTrend: {
      loading: true,
      noData: false,
      data: EMPTY_CHART_DATA,
      visibility: {},
    },
    eventAmountByDataSource: {
      loading: true,
      noData: false,
      data: EMPTY_CHART_DATA,
      visibility: {},
    },
    eventAndTotalAmountByDataSource: {
      loading: true,
      noData: false,
      data: EMPTY_CHART_DATA,
      visibility: {},
    },
    topPositionsByEventAmount: {
      loading: true,
      noData: false,
      data: EMPTY_CHART_DATA,
      visibility: {},
    },
    eventAndTotalAmountByBatch: {
      loading: true,
      noData: false,
      data: EMPTY_CHART_DATA,
      visibility: {},
    },
    loadErrorRecordCount: {
      loading: true,
      noData: false,
      data: EMPTY_CHART_DATA,
      visibility: {},
    },
  });
  const handleDropdownChange = (value, name) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getDashboardCompPlanList = React.useCallback(
    async (req) => {
      setDashboardCompPlanFields((prev) => ({ ...prev, isComPlanValueDataLoading: true }));
      if (!req.in_period_end_date) {
        setDashboardCompPlanFields({
          complanValue: [],
          complanValueData: [],
          compPlanDataList: [],
          isComPlanValueDataLoading: false,
        });
        return;
      }
      try {
        const resName = await Action.getEventCompPlanDataList(req);
        if (Array.isArray(resName) && resName.length > 0) {
          const tempArray = resName.map((item) => ({
            text: item.comp_plan_name,
            id: item.comp_plan_mstr_key,
          }));
          setDashboardCompPlanFields({
            complanValueData: tempArray,
            compPlanDataList: tempArray,
            isComPlanValueDataLoading: false,
            complanValue: [{ text: "Select All", id: "Select All" }],
          });
        } else {
          setDashboardCompPlanFields({
            complanValueData: [],
            compPlanDataList: [],
            isComPlanValueDataLoading: false,
            complanValue: [],
          });
        }
      } catch {
        setDashboardCompPlanFields((prev) => ({ ...prev, isComPlanValueDataLoading: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const getDashboardPositionList = React.useCallback(
    async (req) => {
      setDashboardPositionFields((prev) => ({ ...prev, isPositionDataLoading: true }));
      const compObj = {
        in_tenant_id: list.cookiesData.out_tenant_id,
        in_filter: null,
        in_period_start_date: req.in_period_start_date ? convertDate(req.in_period_start_date) : "",
        in_period_end_date: req.in_period_end_date ? convertDate(req.in_period_end_date) : "",
        in_data_source_filter: req.in_data_source_filter,
        in_service_loc_filter: req.in_data_source_loc_filter || 0,
        in_event_type_mstr_key: req.in_event_type_mstr_key,
        in_sort_number: 1,
        in_sort_order: "ASC",
      };
      try {
        const resName = await Action.getEventPositionDataList(req);
        if (Array.isArray(resName) && resName.length > 0) {
          const tempArray = resName.map((item) => ({
            text: item.position_name,
            id: item.org_position_key,
          }));
          setDashboardPositionFields({
            positionData: tempArray,
            positionDataList: tempArray,
            isPositionDataLoading: false,
            positionValue: [{ text: "Select All", id: "Select All" }],
          });
          getDashboardCompPlanList(compObj);
        } else {
          setDashboardPositionFields({
            positionData: [],
            positionDataList: [],
            isPositionDataLoading: false,
            positionValue: [],
          });
          getDashboardCompPlanList(compObj);
        }
      } catch {
        setDashboardPositionFields((prev) => ({ ...prev, isPositionDataLoading: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.cookiesData.out_tenant_id, getDashboardCompPlanList]
  );

  const getDashboardServiceLocationList = React.useCallback(
    async (req) => {
      setDashboardServiceLocationFields((prev) => ({ ...prev, isServiceLocationDataLoading: true }));
      try {
        const resName = await Action.getEventServiceLocationDataList(req);
        if (Array.isArray(resName) && resName.length > 0) {
          const tempArray = resName
            .map((item) => {
              const serviceLocation =
                item.service_location ??
                item.service_location_name ??
                item.event_service_location ??
                item.location_name ??
                item.text;

              return serviceLocation
                ? {
                    text: serviceLocation,
                    id: item.service_location_mstr_key ?? item.id ?? serviceLocation,
                  }
                : null;
            })
            .filter(Boolean);
          setDashboardServiceLocationFields({
            serviceLocationData: tempArray,
            serviceLocationDataList: tempArray,
            isServiceLocationDataLoading: false,
            serviceLocationValue: [{ text: "Select All", id: "Select All" }],
          });
          const positionReq = {
            in_tenant_id: list.cookiesData.out_tenant_id,
            in_filter: null,
            in_period_start_date: req.in_period_start_date || "",
            in_period_end_date: req.in_period_end_date || "",
            in_event_type_mstr_key: req.in_event_type_mstr_key,
            in_data_source_filter: req.in_data_source_filter,
            in_data_source_loc_filter: 0,
            in_sort_number: 1,
            in_sort_order: "ASC",
          };
          getDashboardPositionList(positionReq);
        } else {
          setDashboardServiceLocationFields({
            serviceLocationData: [],
            serviceLocationDataList: [],
            isServiceLocationDataLoading: false,
            serviceLocationValue: [],
          });
          const positionReq = {
            in_tenant_id: list.cookiesData.out_tenant_id,
            in_filter: null,
            in_period_start_date: req.in_period_start_date || "",
            in_period_end_date: req.in_period_end_date || "",
            in_event_type_mstr_key: req.in_event_type_mstr_key,
            in_data_source_filter: req.in_data_source_filter,
            in_data_source_loc_filter: 0,
            in_sort_number: 1,
            in_sort_order: "ASC",
          };
          getDashboardPositionList(positionReq);
        }
      } catch {
        setDashboardServiceLocationFields((prev) => ({ ...prev, isServiceLocationDataLoading: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.cookiesData.out_tenant_id, getDashboardPositionList]
  );

  const getDashboardEventDataSourceList = React.useCallback(
    async (req) => {
      setDashboardEventDataSourceFields((prev) => ({ ...prev, isEventDataSourceDataLoading: true }));
      try {
        const resName = await Action.getEventDataSourceDataList(req);
        const tempArray = mapEventDataSourceOptions(resName);
        if (tempArray.length > 0) {
          setDashboardEventDataSourceFields({
            eventDataSourceData: tempArray,
            eventDataSourceDataList: tempArray,
            isEventDataSourceDataLoading: false,
            eventDataSourceValue: [{ text: "Select All", id: "Select All" }],
          });
          const locationReq = {
            in_tenant_id: list.cookiesData.out_tenant_id,
            in_filter: null,
            in_period_start_date: req.in_period_start_date || "",
            in_period_end_date: req.in_period_end_date || "",
            in_event_type_mstr_key: req.in_event_type_mstr_key,
            in_data_source_filter: 0,
            in_sort_number: 1,
            in_sort_order: "ASC",
          };
          getDashboardServiceLocationList(locationReq);
        } else {
          setDashboardEventDataSourceFields({
            eventDataSourceData: [],
            eventDataSourceDataList: [],
            isEventDataSourceDataLoading: false,
            eventDataSourceValue: [],
          });
          const locationReq = {
            in_tenant_id: list.cookiesData.out_tenant_id,
            in_filter: null,
            in_period_start_date: req.in_period_start_date || "",
            in_period_end_date: req.in_period_end_date || "",
            in_event_type_mstr_key: req.in_event_type_mstr_key,
            in_data_source_filter: 0,
            in_sort_number: 1,
            in_sort_order: "ASC",
          };
          getDashboardServiceLocationList(locationReq);
        }
      } catch {
        setDashboardEventDataSourceFields((prev) => ({ ...prev, isEventDataSourceDataLoading: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.cookiesData.out_tenant_id, getDashboardServiceLocationList]
  );

  const handleDashboardDateRangeChange = (e) => {
    const startDate = e.value?.start ?? null;
    const endDate = e.value?.end ?? null;
    const newDateFields = {
      ...dashboardDateFields,
      in_period_start_date: startDate,
      in_period_end_date: endDate,
    };
    setDashboardDateErrors({ in_period_start_date: "", in_period_end_date: "" });
    setDashboardDateFields(newDateFields);
    if (isYearValidate(startDate) && isYearValidate(endDate)) {
      const eventTypeId = filters.filterEventTypeValue?.id ?? filters.filterEventTypeValue;
      getDashboardEventDataSourceList({
        in_tenant_id: list.cookiesData.out_tenant_id,
        in_filter: null,
        in_period_start_date: convertDate(startDate),
        in_period_end_date: convertDate(endDate),
        in_event_type_mstr_key: eventTypeId,
        in_data_source_filter: 0,
        in_sort_number: 1,
        in_sort_order: "ASC",
      });
    }
  };

  const handleFilterEventTypeChange = (value, name) => {
    handleDropdownChange(value, name);

    if (
      isYearValidate(dashboardDateFields.in_period_start_date) &&
      isYearValidate(dashboardDateFields.in_period_end_date)
    ) {
      getDashboardEventDataSourceList({
        in_tenant_id: list.cookiesData.out_tenant_id,
        in_filter: null,
        in_period_start_date: convertDate(dashboardDateFields.in_period_start_date),
        in_period_end_date: convertDate(dashboardDateFields.in_period_end_date),
        in_event_type_mstr_key: value?.id ?? value,
        in_data_source_filter: 0,
        in_sort_number: 1,
        in_sort_order: "ASC",
      });
    }
  };

  const selectedEventType = React.useMemo(() => {
    if (!filters.eventTypeValue) return null;
    const filterId = filters.eventTypeValue?.id ?? filters.eventTypeValue;
    return eventTypeList.find((item) => item.id === filterId) || null;
  }, [filters.eventTypeValue, eventTypeList]);

  const selectedFilterEventType = React.useMemo(() => {
    if (!filters.filterEventTypeValue) return null;
    const filterId = filters.filterEventTypeValue?.id ?? filters.filterEventTypeValue;
    return allEventTypeList.find((item) => item.id === filterId) || null;
  }, [filters.filterEventTypeValue, allEventTypeList]);

  const selectedEventSource = React.useMemo(() => {
    if (!filters.eventSourceValue) return null;
    const filterId = filters.eventSourceValue?.id ?? filters.eventSourceValue;
    return eventDataSourceList.find((item) => item.id === filterId) || null;
  }, [filters.eventSourceValue, eventDataSourceList]);

  React.useEffect(() => {
    setFilteredEventDataSourceList(eventDataSourceList);
  }, [eventDataSourceList]);

  const isChartVisibleInUI = React.useCallback(
    (chartId) => {
      if (!chartId) return false;
      const chartConfig = CHART_RENDER_CONFIG[chartId];
      if (chartConfig?.requiresEventSource) {
        // Keep the slot while the source dropdown is (re)loading or whenever the event
        // type has at least one data source, so the grid doesn't reflow as the dropdown
        // resolves after a change. Only hide when there are genuinely no data sources.
        return isEventSourceLoading || eventDataSourceList.length > 0;
      }
      return true;
    },
    [isEventSourceLoading, eventDataSourceList]
  );

  React.useEffect(() => {
    if (expandedChartKey && !isChartVisibleInUI(expandedChartKey)) {
      setExpandedChartKey("");
    }
  }, [expandedChartKey, isChartVisibleInUI]);

  const loadEventDashboardCardData = async (
    eventTypeId = selectedEventType?.id ?? selectedEventType,
    onResult = setEventDashboardCardMetrics
  ) => {
    if (!eventTypeId) {
      onResult({ ...EMPTY_EVENT_DASHBOARD_CARD_METRICS });
      return;
    }

    setIsEventCardLoading(true);
    onResult(null);
    try {
      const metrics = await Action.getEventDashboardCardData({
        in_event_type_mstr_key: eventTypeId,
      });
      onResult(metrics ?? { ...EMPTY_EVENT_DASHBOARD_CARD_METRICS });
    } catch (error) {
      onResult({ ...EMPTY_EVENT_DASHBOARD_CARD_METRICS });
    } finally {
      setIsEventCardLoading(false);
    }
  };

  const loadChartData = async (chartId, eventTypeId = selectedEventType?.id ?? selectedEventType) => {
    const chartConfig = CHART_RENDER_CONFIG[chartId];
    if (!chartConfig) return;

    if (!eventTypeId) {
      setChartStateById((prev) => ({
        ...prev,
        [chartId]: {
          ...prev[chartId],
          noData: true,
          loading: false,
          data: EMPTY_CHART_DATA,
        },
      }));
      return;
    }

    if (!chartConfig.apiMethod || typeof Action[chartConfig.apiMethod] !== "function") {
      setChartStateById((prev) => ({
        ...prev,
        [chartId]: {
          ...prev[chartId],
          loading: false,
          noData: true,
          data: EMPTY_CHART_DATA,
          visibility: {},
        },
      }));
      return;
    }

    setChartStateById((prev) => ({
      ...prev,
      [chartId]: {
        ...prev[chartId],
        loading: true,
      },
    }));

    try {
      const selectedEventSourceId = selectedEventSource?.id ?? selectedEventSource ?? "";
      const payload = chartConfig.getPayload
        ? chartConfig.getPayload(eventTypeId, selectedEventSourceId)
        : { in_event_type_mstr_key: eventTypeId };
      const res = await Action[chartConfig.apiMethod](payload);

      const outRetCode = res?.data?.out_parameters?.out_ret_code;
      const rows = res?.data?.row_result || [];
      const normalizedData = outRetCode === 0 || outRetCode === 2 ? chartConfig.normalize(rows) : EMPTY_CHART_DATA;
      const hasCategories = Array.isArray(normalizedData.categories) && normalizedData.categories.length > 0;
      const hasSeriesData =
        Array.isArray(normalizedData.series) &&
        normalizedData.series.some(
          (seriesItem) =>
            Array.isArray(seriesItem?.data) &&
            seriesItem.data.some((point) => point !== null && point !== undefined && Number.isFinite(Number(point)))
        );

      setChartStateById((prev) => {
        const prevVisibility = prev[chartId]?.visibility || {};
        const nextVisibility = {};
        normalizedData.series.forEach((series) => {
          nextVisibility[series.name] = prevVisibility[series.name] ?? true;
        });

        return {
          ...prev,
          [chartId]: {
            ...prev[chartId],
            loading: false,
            noData: !(hasCategories && hasSeriesData),
            data: normalizedData,
            visibility: nextVisibility,
          },
        };
      });
    } catch (error) {
      setChartStateById((prev) => ({
        ...prev,
        [chartId]: {
          ...prev[chartId],
          loading: false,
          noData: true,
          data: EMPTY_CHART_DATA,
        },
      }));
    }
  };

  const loadAllDashboardData = (eventTypeId = selectedEventType?.id ?? selectedEventType, onCardResult) => {
    loadEventDashboardCardData(eventTypeId, onCardResult || setEventDashboardCardMetrics);
    Object.keys(CHART_RENDER_CONFIG).forEach((chartId) => {
      if (chartId === "eventAmountByDataSource" || chartId === "eventAndTotalAmountByDataSource") {
        return;
      }
      loadChartData(chartId, eventTypeId);
    });
  };

  const processedVsNewChart = chartStateById?.processedVsNewRecords;
  const processedRecordsTotal = React.useMemo(
    () => sumSeriesDataByName(processedVsNewChart?.data?.series, "Processed Records"),
    [processedVsNewChart?.data]
  );
  const newRecordsTotal = React.useMemo(
    () => sumSeriesDataByName(processedVsNewChart?.data?.series, "New Records"),
    [processedVsNewChart?.data]
  );

  const summaryTilesRow = React.useMemo(() => {
    const m = eventDashboardCardMetrics;

    const tiles = [
      {
        key: "totalRecords",
        title: "Records",
        countTooltip: "Processed + New over the rolling last 12 months.",
        tooltip: "Processed + New over the rolling last 12 months.",
        badge: "Rolling 12 months",
        value: isEventCardLoading || m === null ? null : formatEventRecordCount(m.total_records),
      },
      {
        key: "processedRecords",
        title: "Processed",
        countTooltip: "Processed event records, rolling last 12 months.",
        tooltip: "Count of processed event records over the rolling last 12 months.",
        badge: "Rolling 12 months",
        value: isEventCardLoading || m === null ? null : formatEventRecordCount(m.processed_records),
      },
      {
        key: "newRecords",
        title: "New",
        countTooltip: "New event records, rolling last 12 months.",
        tooltip: "Count of new event records over the rolling last 12 months.",
        badge: "Rolling 12 months",
        value: isEventCardLoading || m === null ? null : formatEventRecordCount(m.new_records),
      },
      {
        key: "unassignedRecords",
        title: "Unassigned",
        countTooltip: "Records where the position is the default or named Unassigned, rolling last 12 months.",
        tooltip:
          "Count of event records tied to the default position or a position named Unassigned, over the rolling last 12 months.",
        badge: "Rolling 12 months",
        value: isEventCardLoading || m === null ? null : formatEventRecordCount(m.unassigned_records),
      },
    ];

    const isTileLoading = isEventCardLoading || isEventTypeLoading || eventDashboardCardMetrics === null;

    return (
      <TileLayout
        columns={4}
        rowHeight={110}
        columnWidth={200}
        gap={{ rows: 3, columns: 5 }}
        className="tilelayout eventdash-tilelayout eventdashboard-tile-cont"
        ignoreDrag={() => true}
        items={tiles.map((tile) => ({
          ...SUMMARY_TILE_LAYOUT_ITEM_OPTIONS,
          className: `eventdash-summary-tile eventdash-summary-tile--${tile.key}`,
          header: (
            <>
              <div className={`eventdash-summary-tile-icon eventdash-summary-tile-icon--${tile.key}`}>
                <EventDashSummaryTileIcon type={tile.key} />
              </div>
              <Tooltip title={tile.tooltip} anchorElement="target" position="bottom" openDelay={100}>
                <span>{tile.title}</span>
              </Tooltip>
            </>
          ),
          body: (
            <div style={{ textAlign: "center" }}>
              {isTileLoading ? (
                <Skeleton
                  shape="text"
                  className="eventdash-summary-tile-value-skeleton"
                  style={{ width: 72, height: 24, margin: "0 auto" }}
                />
              ) : (
                <h3 className="darkgraytitle hover-wrapper eventdash-summary-tile-value">
                  <Tooltip title={tile.countTooltip} anchorElement="target" position="bottom" openDelay={100}>
                    <span className="eventdash-summary-tile-value-text" title={tile.countTooltip}>
                      {tile.value ?? "—"}
                    </span>
                  </Tooltip>
                </h3>
              )}
            </div>
          ),
        }))}
      />
    );
  }, [eventDashboardCardMetrics, isEventCardLoading, isEventTypeLoading]);

  React.useEffect(() => {
    let isMounted = true;

    const loadEventTypes = async () => {
      setIsEventTypeLoading(true);
      try {
        const req = {
          in_tenant_id: list.cookiesData.out_tenant_id,
          in_filter: null,
          in_sort_number: 3,
          in_sort_order: "ASC",
          in_page_row_offset: 0,
          in_page_rows: 9999,
        };

        const res = await Action.getEventTypeDataList(req);
        if (!isMounted) return;

        const tempArray = (res || []).map((item) => ({
          text: item.event_type_name,
          id: item.event_type_mstr_key,
        }));
        const dashboardTempArray = (res || [])
          .filter((item) => Number(item.event_dashboard_flag) === 1)
          .map((item) => ({
            text: item.event_type_name,
            id: item.event_type_mstr_key,
          }));

        setEventTypeList(dashboardTempArray);
        setAllEventTypeList(tempArray);
        setFilterEventTypeDisplayList(tempArray);
        setFilters((prev) => ({
          ...prev,
          eventTypeValue: dashboardTempArray[0] ?? null,
          filterEventTypeValue: tempArray[0] ?? null,
          eventSourceValue: null,
        }));
      } finally {
        if (isMounted) {
          setIsEventTypeLoading(false);
        }
      }
    };

    loadEventTypes();

    return () => {
      isMounted = false;
    };
  }, [list.cookiesData.out_tenant_id]);

  React.useEffect(() => {
    let isMounted = true;

    const loadEventSources = async () => {
      if (!filters.eventTypeValue) return;
      setIsEventSourceLoading(true);
      try {
        const periodEndDate = new Date();
        const periodStartDate = new Date();
        periodStartDate.setMonth(periodStartDate.getMonth() - 12);

        const req = {
          in_tenant_id: list.cookiesData.out_tenant_id,
          in_filter: null,
          in_period_start_date: convertDate(periodStartDate),
          in_period_end_date: convertDate(periodEndDate),
          in_event_type_mstr_key: filters.eventTypeValue?.id ?? filters.eventTypeValue,
          in_data_source_filter: 0,
          in_sort_number: 1,
          in_sort_order: "ASC",
        };

        const res = await Action.getEventDataSourceDataList(req);
        if (!isMounted) return;

        const tempArray = mapEventDataSourceOptions(res);

        setEventDataSourceList(tempArray);
        setFilters((prev) => ({
          ...prev,
          eventSourceValue: tempArray[0] ?? null,
        }));
      } finally {
        if (isMounted) {
          setIsEventSourceLoading(false);
        }
      }
    };

    loadEventSources();

    return () => {
      isMounted = false;
    };
  }, [filters.eventTypeValue, list.cookiesData.out_tenant_id]);

  React.useEffect(() => {
    let isMounted = true;
    const eventTypeId = selectedEventType?.id ?? selectedEventType;

    if (!eventTypeId) {
      if (isEventTypeLoading) {
        setEventDashboardCardMetrics(null);
        setIsEventCardLoading(true);
        setChartStateById((prev) => {
          const next = { ...prev };
          Object.keys(CHART_RENDER_CONFIG).forEach((chartId) => {
            next[chartId] = {
              ...next[chartId],
              loading: true,
              noData: false,
              data: EMPTY_CHART_DATA,
            };
          });
          return next;
        });
        return undefined;
      }

      setEventDashboardCardMetrics({ ...EMPTY_EVENT_DASHBOARD_CARD_METRICS });
      setIsEventCardLoading(false);
      setChartStateById((prev) => {
        const next = { ...prev };
        Object.keys(CHART_RENDER_CONFIG).forEach((chartId) => {
          next[chartId] = {
            ...next[chartId],
            loading: false,
            noData: true,
            data: EMPTY_CHART_DATA,
          };
        });
        return next;
      });
      return undefined;
    }

    if (isEventTypeLoading) return undefined;

    loadAllDashboardData(eventTypeId, (value) => {
      if (isMounted) {
        setEventDashboardCardMetrics(value);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [selectedEventType, isEventTypeLoading]);

  React.useEffect(() => {
    const eventTypeId = selectedEventType?.id ?? selectedEventType;

    if (!eventTypeId || isEventTypeLoading) return;

    // Source dropdown is (re)loading after an event-type change — show the data-source
    // charts as loading so stale data from the previous event type isn't left on screen.
    if (isEventSourceLoading) {
      setChartStateById((prev) => ({
        ...prev,
        eventAmountByDataSource: {
          ...prev.eventAmountByDataSource,
          loading: true,
          noData: false,
        },
        eventAndTotalAmountByDataSource: {
          ...prev.eventAndTotalAmountByDataSource,
          loading: true,
          noData: false,
        },
      }));
      return;
    }

    const selectedEventSourceId = selectedEventSource?.id ?? selectedEventSource ?? "";
    if (!selectedEventSourceId) {
      setChartStateById((prev) => ({
        ...prev,
        eventAmountByDataSource: {
          ...prev.eventAmountByDataSource,
          loading: false,
          noData: true,
          data: EMPTY_CHART_DATA,
          visibility: {},
        },
        eventAndTotalAmountByDataSource: {
          ...prev.eventAndTotalAmountByDataSource,
          loading: false,
          noData: true,
          data: EMPTY_CHART_DATA,
          visibility: {},
        },
      }));
      return;
    }

    loadChartData("eventAmountByDataSource", eventTypeId);
    loadChartData("eventAndTotalAmountByDataSource", eventTypeId);
  }, [selectedEventSource, selectedEventType, isEventTypeLoading, isEventSourceLoading]);

  const getChartTitle = (chartId) => CHART_TITLES[chartId];

  const loadLandingDashboardData = React.useCallback(async () => {
    const req = {
      in_tenant_id: list.cookiesData.out_tenant_id,
      in_filter: null,
      in_sort_number: 1,
      in_sort_order: "DESC",
    };

    const res = await Action.getLandingDashboardData(req);
    const retCode = res?.data?.out_parameters?.out_ret_code;
    const rows = res?.data?.row_result || [];
    const eventDashboardRows = rows.filter(
      (item) => Number(item.object_type_mstr_key ?? item.object_type) === EVENT_DASHBOARD_OBJECT_TYPE
    );

    if (!(retCode === 0 || retCode === 2) || eventDashboardRows.length === 0) {
      return;
    }

    const orderedRows = eventDashboardRows
      .map((item) => ({
        chartId: CHART_ID_BY_LABEL[item.label_name],
        podOrder: Number(item.in_pod_order ?? item.pod_order ?? item.order_no ?? 0),
        dashboardEventTypeKey: item.dashboard_event_type_mstr_key ?? null,
      }))
      .filter((item) => item.chartId)
      .sort((a, b) => a.podOrder - b.podOrder);

    const loadedOrder = orderedRows.map((item) => item.chartId);
    setBaseChartOrder(normalizeChartOrder(loadedOrder));

    setLandingPodEventTypeKeyByChartId(() => {
      const map = {};
      orderedRows.forEach((item) => {
        map[item.chartId] = item.dashboardEventTypeKey;
      });
      return map;
    });

    setChartLayouts((prev) => {
      const nextLayouts = { ...prev };
      eventDashboardRows.forEach((item) => {
        const chartId = CHART_ID_BY_LABEL[item.label_name];
        if (!chartId) return;
        nextLayouts[chartId] = coerceLayoutOption(getLayoutOption(item.layout_value));
      });
      return nextLayouts;
    });
  }, [list.cookiesData.out_tenant_id]);

  React.useEffect(() => {
    loadLandingDashboardData();
  }, [loadLandingDashboardData]);

  React.useEffect(() => {
    const selectedEventTypeId = selectedEventType?.id ?? selectedEventType;

    if (!selectedEventTypeId) {
      setChartOrder(baseChartOrder);
      return;
    }

    const visibleOrder = baseChartOrder.filter((chartId) => {
      const dashboardEventTypeKey = landingPodEventTypeKeyByChartId?.[chartId];
      if (isNullLikeDashboardEventTypeKey(dashboardEventTypeKey)) {
        return true;
      }
      return String(dashboardEventTypeKey) === String(selectedEventTypeId);
    });

    setChartOrder(visibleOrder);
  }, [baseChartOrder, landingPodEventTypeKeyByChartId, selectedEventType]);

  const updateChartLayout = async (chartId, layoutValue, podOrder) => {
    const labelName = CHART_LABEL_MAP[chartId];
    if (!labelName) return;

    await Action.updateChartLayout({
      in_tenant_id: list.cookiesData.out_tenant_id,
      in_user_id: list.cookiesData.out_user_id,
      label_name: labelName,
      layout_value: layoutValue,
      in_pod_order: podOrder,
    });
  };

  const renderChartHeader = (title, chartKey, headerChartState) => {
    return (
      <h5 className="chart-title">
        <div className="charttitle-left eventdash-chart-title-wrap">
          <span className="eventdash-chart-title-text" title={title}>
            {title}
          </span>
          {chartKey === "eventAndTotalAmountByDataSource" ||
          chartKey === "eventAmountByDataSource" ? (
            <div className="eventdash-inline-source-filter">
              <CustomDropdown
                name="eventSourceValue"
                data={filteredEventDataSourceList}
                dataList={eventDataSourceList}
                textField="text"
                dataItemKey="id"
                className="eventdash-source-dropdown"
                valueData={selectedEventSource}
                onChange={handleDropdownChange}
                onFilterChange={setFilteredEventDataSourceList}
                onClose={() => setFilteredEventDataSourceList(eventDataSourceList)}
                filterable={true}
                loading={isEventSourceLoading}
              />
            </div>
          ) : null}
          <Tooltip
            title={CHART_INFO_TOOLTIPS[chartKey] || title}
            anchorElement="target"
            position="bottom"
            openDelay={100}
          >
            <svg
              fill="#989898"
              width="16px"
              height="16px"
              viewBox="0 0 1920 1920"
              xmlns="http://www.w3.org/2000/svg"
              className="eventdash-info-icon"
              title={CHART_INFO_TOOLTIPS[chartKey] || title}
            >
              <path
                d="M960 0c530.193 0 960 429.807 960 960s-429.807 960-960 960S0 1490.193 0 960 429.807 0 960 0Zm0 101.053c-474.384 0-858.947 384.563-858.947 858.947S485.616 1818.947 960 1818.947 1818.947 1434.384 1818.947 960 1434.384 101.053 960 101.053Zm-42.074 626.795c-85.075 39.632-157.432 107.975-229.844 207.898-10.327 14.249-10.744 22.907-.135 30.565 7.458 5.384 11.792 3.662 22.656-7.928 1.453-1.562 1.453-1.562 2.94-3.174 9.391-10.17 16.956-18.8 33.115-37.565 53.392-62.005 79.472-87.526 120.003-110.867 35.075-20.198 65.9 9.485 60.03 47.471-1.647 10.664-4.483 18.534-11.791 35.432-2.907 6.722-4.133 9.646-5.496 13.23-13.173 34.63-24.269 63.518-47.519 123.85l-1.112 2.886c-7.03 18.242-7.03 18.242-14.053 36.48-30.45 79.138-48.927 127.666-67.991 178.988l-1.118 3.008a10180.575 10180.575 0 0 0-10.189 27.469c-21.844 59.238-34.337 97.729-43.838 138.668-1.484 6.37-1.484 6.37-2.988 12.845-5.353 23.158-8.218 38.081-9.82 53.42-2.77 26.522-.543 48.24 7.792 66.493 9.432 20.655 29.697 35.43 52.819 38.786 38.518 5.592 75.683 5.194 107.515-2.048 17.914-4.073 35.638-9.405 53.03-15.942 50.352-18.932 98.861-48.472 145.846-87.52 41.11-34.26 80.008-76 120.788-127.872 3.555-4.492 3.555-4.492 7.098-8.976 12.318-15.707 18.352-25.908 20.605-36.683 2.45-11.698-7.439-23.554-15.343-19.587-3.907 1.96-7.993 6.018-14.22 13.872-4.454 5.715-6.875 8.77-9.298 11.514-9.671 10.95-19.883 22.157-30.947 33.998-18.241 19.513-36.775 38.608-63.656 65.789-13.69 13.844-30.908 25.947-49.42 35.046-29.63 14.559-56.358-3.792-53.148-36.635 2.118-21.681 7.37-44.096 15.224-65.767 17.156-47.367 31.183-85.659 62.216-170.048 13.459-36.6 19.27-52.41 26.528-72.201 21.518-58.652 38.696-105.868 55.04-151.425 20.19-56.275 31.596-98.224 36.877-141.543 3.987-32.673-5.103-63.922-25.834-85.405-22.986-23.816-55.68-34.787-96.399-34.305-45.053.535-97.607 15.256-145.963 37.783Zm308.381-388.422c-80.963-31.5-178.114 22.616-194.382 108.33-11.795 62.124 11.412 115.76 58.78 138.225 93.898 44.531 206.587-26.823 206.592-130.826.005-57.855-24.705-97.718-70.99-115.729Z"
                fillRule="evenodd"
              />
            </svg>
          </Tooltip>
        </div>
        <div className="eventdash-chart-actions">
          <Button
            title="Expand Chart"
            className="expandchartBtn"
            svgIcon={toggleFullScreenModeIcon}
            onClick={() => setExpandedChartKey(chartKey)}
          />
        </div>
      </h5>
    );
  };

  const renderChartCard = (chartId, heightClass) => {
    if (!isChartVisibleInUI(chartId)) return null;

    const chartState = chartStateById[chartId];

    return (
      <div id={chartId} className={`eventdash-chart-slot ${chartLayouts[chartId]?.value || "flex30"}`}>
        <div className="eventdash-chart-box">
          {renderChartHeader(getChartTitle(chartId), chartId, chartState)}
          <div className={`eventdash-chart-body ${heightClass}`}>{renderChartContent(chartId)}</div>
        </div>
      </div>
    );
  };

  const renderChartContent = (chartId, isExpanded = false) => {
    const chartConfig = CHART_RENDER_CONFIG[chartId];
    if (!chartConfig) {
      return <div className="eventdash-chart-placeholder" />;
    }

    const chartState = chartStateById[chartId];
    const series = chartState?.data?.series || [];
    const categories = chartState?.data?.categories || [];
    const categoryTooltips = chartState?.data?.categoryTooltips || {};
    const visibility = chartState?.visibility || {};

    const handleLegendItemClick = (seriesName) => {
      setChartStateById((prev) => {
        const currentVisibility = prev[chartId]?.visibility || {};
        const currentlyVisible = Object.values(currentVisibility).filter(Boolean).length;
        const isCurrentSeriesVisible = currentVisibility[seriesName] !== false;
        if (isCurrentSeriesVisible && currentlyVisible <= 1) {
          return prev;
        }

        return {
          ...prev,
          [chartId]: {
            ...prev[chartId],
            visibility: {
              ...currentVisibility,
              [seriesName]: !isCurrentSeriesVisible,
            },
          },
        };
      });
    };

    return (
      <DashboardChart
        categories={categories}
        categoryTooltips={categoryTooltips}
        series={series}
        loading={Boolean(chartState?.loading)}
        noData={Boolean(chartState?.noData)}
        isExpanded={isExpanded}
        visibility={visibility}
        onToggleSeries={handleLegendItemClick}
        valueFormatter={chartConfig.valueFormatter}
        tooltipValueFormatter={chartConfig.tooltipValueFormatter}
        tooltipShowCategory={chartConfig.tooltipShowCategory}
        secondaryValueFormatter={chartConfig.secondaryValueFormatter}
        categoryFormatter={chartConfig.categoryFormatter || formatBatchAxisLabel}
        labelRotation={chartConfig.labelRotation}
        valueLabelRotation={chartConfig.valueLabelRotation}
        useDualAxis={Boolean(chartConfig.useDualAxis)}
        syncDualAxisScale={Boolean(chartConfig.syncDualAxisScale)}
        leftAxisTitle={chartConfig.leftAxisTitle || ""}
        rightAxisTitle={chartConfig.rightAxisTitle || ""}
        leftAxisMajorUnit={chartConfig.leftAxisMajorUnit}
        categoryAxisTitle={chartConfig.categoryAxisTitle || ""}
        isPieChart={Boolean(chartConfig.isPieChart)}
        invertCategoryOrder={Boolean(chartConfig.invertCategoryOrder)}
        horizontalBarChart={Boolean(chartConfig.horizontalBarChart)}
        showLegend={chartConfig.showLegend !== false}
        barGap={chartConfig.barGap}
        barSpacing={chartConfig.barSpacing}
      />
    );
  };

  const rightSet = new Set(RIGHT_PANEL_CHART_IDS);
  const rightChartIds = chartOrder.filter((chartId) => rightSet.has(chartId) && isChartVisibleInUI(chartId));

  const availableLeftFromOrder = chartOrder.filter((id) => !rightSet.has(id) && isChartVisibleInUI(id));
  const leftChartIds = availableLeftFromOrder.slice(0, 4);
  const topRowChartIds = leftChartIds.slice(0, 2);
  const bottomRowChartIds = leftChartIds.slice(2, 4);

  const getEventScreenFilterState = () => ({
    autoSearch: true,
    in_period_start_date: dashboardDateFields.in_period_start_date,
    in_period_end_date: dashboardDateFields.in_period_end_date,
    filterEventTypeValue: selectedFilterEventType || filters.filterEventTypeValue,
    eventDataSourceValue: dashboardEventDataSourceFields.eventDataSourceValue,
    serviceLocationValue: dashboardServiceLocationFields.serviceLocationValue,
    positionValue: dashboardPositionFields.positionValue,
    complanValue: dashboardCompPlanFields.complanValue,
  });

  const getDisableDashboardSearchBtn = () => {
    const loadingState =
      dashboardCompPlanFields.isComPlanValueDataLoading ||
      dashboardPositionFields.isPositionDataLoading ||
      dashboardServiceLocationFields.isServiceLocationDataLoading ||
      dashboardEventDataSourceFields.isEventDataSourceDataLoading;

    const dataState =
      !dashboardEventDataSourceFields.eventDataSourceDataList.length &&
      !dashboardServiceLocationFields.serviceLocationDataList.length &&
      !dashboardPositionFields.positionDataList.length &&
      !dashboardCompPlanFields.compPlanDataList.length;

    return loadingState || dataState;
  };

  const handleDashboardSearch = () => {
    const nextErrors = { in_period_start_date: "", in_period_end_date: "" };
    const start = dashboardDateFields.in_period_start_date;
    const end = dashboardDateFields.in_period_end_date;

    if (!start) nextErrors.in_period_start_date = "Period start date is required";
    if (!end) nextErrors.in_period_end_date = "Period end date is required";
    if (start && !isYearValidate(start)) nextErrors.in_period_start_date = "Period start date is not valid";
    if (end && !isYearValidate(end)) nextErrors.in_period_end_date = "Period end date is not valid";
    if (start && end && start > end) nextErrors.in_period_end_date = "Period end date must be greater than start date";

    setDashboardDateErrors(nextErrors);
    if (nextErrors.in_period_start_date || nextErrors.in_period_end_date) return;

    const eventTypeId = filters.filterEventTypeValue?.id ?? filters.filterEventTypeValue;
    if (!eventTypeId) return;

    const eventDashboardNavigationKey = `${Date.now()}-${Math.random()}`;
    sessionStorage.setItem("eventDashboardNavigationKey", eventDashboardNavigationKey);
    history.push("/events", {
      fromEventDashboard: true,
      eventDashboardNavigationKey,
      eventDashboardFilters: getEventScreenFilterState(),
    });
  };

  const ViewEventsLink = () => {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <a
          className="cta-small view-prov-position-btn"
          onClick={(e) => {
            e.preventDefault();
            history.push("/events");
          }}
          title="View Event Records"
        >
          View Event Records <i className="fas fa-arrow-right"></i>
        </a>
      </div>
    );
  };

  return (
    <div className="Main eventspage event-drp eventdashboard">
      <div className="content">
        <div className="content-head eventhead">
          <HeaderPage
            pageProps={pageProps}
            headerConfig={{
              title: "Event Dashboard",
              id: "event_dashboard",
              // View Event Records is a read-only navigation link, so it must live in a
              // non-write-gated slot (header_actions_fx) instead of add_new_fx. Otherwise it
              // disappears when the role's WRITE access is removed; it should only be hidden
              // when READ access is removed (which makes the whole dashboard inaccessible).
              header_actions_fx: ViewEventsLink,
            }}
          />
        </div>

        <div className="content-body eventdashboard">
          <div className="filtercontainer">
            <div className="eventdash-filter-row">
              <div className="eventdash-filter-bar eventdash-filter-bar--grid">
                <div className="eventdash-filter-section eventdash-filter-section-grid">
                  <span className="eventdash-filter-section-title">
                    <p className="gridviewtitle">Grid View</p>
                    <p className="gridviewsubtitle">
                      Filters and opens the data grid. Doesn't change the charts below
                    </p>
                  </span>
                  <div className="eventdash-filter-section-controls">
                    <div className={"k-form-field-wrap-popup valueDropdown eventdash-daterange datepickerstyle"}>
                      <DateRangePicker
                        value={{
                          start: dashboardDateFields.in_period_start_date || null,
                          end: dashboardDateFields.in_period_end_date || null,
                        }}
                        onChange={handleDashboardDateRangeChange}
                        startDateInputSettings={startDateInputSettings}
                        endDateInputSettings={endDateInputSettings}
                      />
                      {dashboardDateErrors.in_period_start_date ? (
                        <span className="k-form-error">{dashboardDateErrors.in_period_start_date}</span>
                      ) : null}
                      {dashboardDateErrors.in_period_end_date ? (
                        <span className="k-form-error">{dashboardDateErrors.in_period_end_date}</span>
                      ) : null}
                    </div>
                    <div className={"k-form-field-wrap-popup widtheventype"}>
                      <Label>{"Event Type:"}</Label>
                      <CustomDropdown
                        name="filterEventTypeValue"
                        data={filterEventTypeDisplayList}
                        dataList={allEventTypeList}
                        textField="text"
                        dataItemKey="id"
                        valueData={selectedFilterEventType}
                        onChange={handleFilterEventTypeChange}
                        onOpen={() => setFilterEventTypeDisplayList(allEventTypeList)}
                        onFilterChange={(dataValue) => setFilterEventTypeDisplayList(dataValue)}
                        filterable={true}
                        loading={isEventTypeLoading}
                      />
                    </div>
                    <div className={"k-form-field-wrap-popup valueDropdown"}>
                      <Label>{"Event Data Source:"}</Label>
                      <MultiSelectDropdown
                        name={"eventDataSourceValue"}
                        module="eventDataSourceValue"
                        data={dashboardEventDataSourceFields.eventDataSourceData}
                        textField="text"
                        onOpen={(data) => {
                          setDashboardEventDataSourceFields((prev) => ({ ...prev, eventDataSourceData: data }));
                        }}
                        dataItemKey="id"
                        valueData={dashboardEventDataSourceFields.eventDataSourceValue}
                        onChange={(e, name) => {
                          setDashboardEventDataSourceFields((prev) => ({ ...prev, [name]: e }));
                          const locationReq = {
                            in_tenant_id: list.cookiesData.out_tenant_id,
                            in_filter: null,
                            in_period_start_date: dashboardDateFields.in_period_start_date
                              ? convertDate(dashboardDateFields.in_period_start_date)
                              : "",
                            in_period_end_date: dashboardDateFields.in_period_end_date
                              ? convertDate(dashboardDateFields.in_period_end_date)
                              : "",
                            in_event_type_mstr_key: filters.filterEventTypeValue?.id ?? filters.filterEventTypeValue,
                            in_data_source_filter: inSourceFilterValue(e),
                            in_sort_number: 1,
                            in_sort_order: "ASC",
                          };
                          getDashboardServiceLocationList(locationReq);
                        }}
                        filterable={true}
                        onFilterChange={(data) => {
                          setDashboardEventDataSourceFields((prev) => ({ ...prev, eventDataSourceData: data }));
                        }}
                        autoClose={false}
                        autoBind={true}
                        isSelectALl={true}
                        dataList={dashboardEventDataSourceFields.eventDataSourceDataList}
                        loading={dashboardEventDataSourceFields.isEventDataSourceDataLoading}
                        disabled={dashboardEventDataSourceFields.isEventDataSourceDataLoading}
                      />
                    </div>
                    <div className={"k-form-field-wrap-popup valueDropdown"}>
                      <Label>{"Service Location:"}</Label>
                      <MultiSelectDropdown
                        name={"serviceLocationValue"}
                        module="serviceLocationValue"
                        data={dashboardServiceLocationFields.serviceLocationData}
                        textField="text"
                        onOpen={(data) => {
                          setDashboardServiceLocationFields((prev) => ({ ...prev, serviceLocationData: data }));
                        }}
                        dataItemKey="id"
                        valueData={dashboardServiceLocationFields.serviceLocationValue}
                        onChange={(data, name) => {
                          setDashboardServiceLocationFields((prev) => ({ ...prev, [name]: data }));
                          const positionReq = {
                            in_tenant_id: list.cookiesData.out_tenant_id,
                            in_filter: null,
                            in_period_start_date: dashboardDateFields.in_period_start_date || "",
                            in_period_end_date: dashboardDateFields.in_period_end_date || "",
                            in_event_type_mstr_key: filters.filterEventTypeValue?.id ?? filters.filterEventTypeValue,
                            in_data_source_filter: inSourceFilterValue(
                              dashboardEventDataSourceFields.eventDataSourceValue
                            ),
                            in_data_source_loc_filter: inSourceFilterValue(data),
                            in_sort_number: 1,
                            in_sort_order: "ASC",
                          };
                          getDashboardPositionList(positionReq);
                        }}
                        filterable={true}
                        onFilterChange={(data) => {
                          setDashboardServiceLocationFields((prev) => ({ ...prev, serviceLocationData: data }));
                        }}
                        autoClose={false}
                        autoBind={true}
                        isSelectALl={true}
                        dataList={dashboardServiceLocationFields.serviceLocationDataList}
                        loading={dashboardServiceLocationFields.isServiceLocationDataLoading}
                        disabled={dashboardServiceLocationFields.isServiceLocationDataLoading}
                      />
                    </div>
                    <div className={"k-form-field-wrap-popup valueDropdown"}>
                      <Label>{"Position:"}</Label>
                      <MultiSelectDropdown
                        name={"positionValue"}
                        module="positionValue"
                        data={dashboardPositionFields.positionData}
                        textField="text"
                        onOpen={(data) => {
                          setDashboardPositionFields((prev) => ({ ...prev, positionData: data }));
                        }}
                        dataItemKey="id"
                        valueData={dashboardPositionFields.positionValue}
                        onChange={(data, name) => {
                          setDashboardPositionFields((prev) => ({ ...prev, [name]: data }));
                        }}
                        filterable={true}
                        onFilterChange={(data) => {
                          setDashboardPositionFields((prev) => ({ ...prev, positionData: data }));
                        }}
                        autoClose={false}
                        autoBind={true}
                        isSelectALl={true}
                        dataList={dashboardPositionFields.positionDataList}
                        loading={dashboardPositionFields.isPositionDataLoading}
                        disabled={dashboardPositionFields.isPositionDataLoading}
                      />
                    </div>
                    <div className={"k-form-field-wrap-popup valueDropdown"}>
                      <Label>{"Comp Plan:"}</Label>
                      <MultiSelectDropdown
                        name={"complanValue"}
                        module="complanValue"
                        data={dashboardCompPlanFields.complanValueData}
                        textField="text"
                        onOpen={(data) => {
                          setDashboardCompPlanFields((prev) => ({ ...prev, complanValueData: data }));
                        }}
                        dataItemKey="id"
                        valueData={dashboardCompPlanFields.complanValue}
                        onChange={(data, name) => {
                          setDashboardCompPlanFields((prev) => ({ ...prev, [name]: data }));
                        }}
                        filterable={true}
                        onFilterChange={(data) => {
                          setDashboardCompPlanFields((prev) => ({ ...prev, complanValueData: data }));
                        }}
                        autoClose={false}
                        autoBind={true}
                        isSelectALl={true}
                        dataList={dashboardCompPlanFields.compPlanDataList}
                        loading={dashboardCompPlanFields.isComPlanValueDataLoading}
                        disabled={dashboardCompPlanFields.isComPlanValueDataLoading}
                      />
                    </div>

                    <Button
                      primary={true}
                      className="searchbtn"
                      disabled={getDisableDashboardSearchBtn()}
                      type={"button"}
                      onClick={() => {
                        handleDashboardSearch();
                      }}
                      title="Search"
                    >
                      <i className="fas fa-search"></i>
                    </Button>
                  </div>
                </div>
              </div>

              <div className="eventdash-filter-bar eventdash-filter-bar--dashboard recentearchbar">
                <button className="k-button recentsearchbtn" onClick={() => setShowRecentSearch(!showRecentSearch)}>
                  <svg
                    className={showRecentSearch ? "rotate-icon open" : "rotate-icon"}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      transform: showRecentSearch ? "rotate(180deg)" : "rotate(270deg)",
                      transition: "transform 0.3s ease",
                    }}
                  >
                    <path
                      d="M5.70711 9.71069C5.31658 10.1012 5.31658 10.7344 5.70711 11.1249L10.5993 16.0123C11.3805 16.7927 12.6463 16.7924 13.4271 16.0117L18.3174 11.1213C18.708 10.7308 18.708 10.0976 18.3174 9.70708C17.9269 9.31655 17.2937 9.31655 16.9032 9.70708L12.7176 13.8927C12.3271 14.2833 11.6939 14.2832 11.3034 13.8927L7.12132 9.71069C6.7308 9.32016 6.09763 9.32016 5.70711 9.71069Z"
                      fill="#525252"
                    />

                    {/* <path
                      d="M9 13L12 16M12 16L15 13M12 16V8M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                      stroke="#525252"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    /> */}
                  </svg>
                  Recent Search
                </button>
                {showRecentSearch && (
                  <div className="eventdash-filter-section eventdash-filter-section-dashboard">
                    <span className="eventdash-filter-section-bottom">
                      <div className="eventdash-recent-search resentsearchsection dashboard-border-shadow resentsearchsection--dashboard">
                        <h5 className="chart-title">Recent Searches</h5>

                        <div className="nodata-overlay" style={{ height: "140px" }}>
                          <h3 className="graytitle">This feature is currently under development.</h3>
                        </div>
                      </div>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="eventdash-adjustments-wrap">
            <div className="eventdash-adjustments-panel">
              <span class="eventdash-filter-section-title dashboardview-title">
                <p className="gridviewtitle">Dashboard View</p>
                <p className="gridviewsubtitle">Changes all cards & charts below</p>
                <div className="k-form-field-wrap-popup widtheventype">
                  <Label className="gridviewtitle">{"Viewing:"}</Label>
                  <CustomDropdown
                    name="eventTypeValue"
                    data={eventTypeList}
                    dataList={eventTypeList}
                    textField="text"
                    dataItemKey="id"
                    valueData={selectedEventType}
                    onChange={handleDropdownChange}
                    onFilterChange={() => {}}
                    filterable={false}
                    loading={isEventTypeLoading}
                    disabled={isEventTypeLoading || eventTypeList.length === 0}
                  />
                </div>
              </span>
              <div className="providerdashbopardcontainer eventdash-provider-strip">
                <div className="tilecontainer eventdash-tilecontainer flex30 dashboard-border-shadow">
                  {summaryTilesRow}
                </div>
                {/* <div className="eventdash-recent-search resentsearchsection flex60 dashboard-border-shadow">
              <h5 className="chart-title">Recent Searches</h5>
              <div className="nodata-overlay" style={{ height: "140px" }}>
                <h3 className="graytitle">This feature is currently under development.</h3>
              </div>
            </div> */}
              </div>
              <div className="eventdash-main-grid">
                <div className="eventdash-left">
                  <div className="eventdash-row-top">
                    {topRowChartIds.map((chartId) => (
                      <React.Fragment key={chartId}>
                        {renderChartCard(chartId, "eventdash-chart-body--top")}
                      </React.Fragment>
                    ))}
                  </div>

                  <div className="eventdash-row-bottom">
                    {bottomRowChartIds.map((chartId) => (
                      <React.Fragment key={chartId}>
                        {renderChartCard(chartId, "eventdash-chart-body--bottom")}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className="eventdash-right eventdash-right-panel">
                  {rightChartIds.slice(0, 2).map((chartId, index) => (
                    <React.Fragment key={chartId}>
                      {renderChartCard(
                        chartId,
                        index === 0 ? "eventdash-chart-body--top" : "eventdash-chart-body--bottom"
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {expandedChartKey ? (
            <Window
              title={getChartTitle(expandedChartKey)}
              onClose={() => setExpandedChartKey("")}
              initialHeight={window.innerHeight - 40}
              initialWidth={window.innerWidth - 40}
              visible={true}
              actions={["Close"]}
              resizable={false}
              titleClassName="dashboard-data-set-title"
            >
              <div className="eventdash-expanded-content">{renderChartContent(expandedChartKey, true)}</div>
            </Window>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default React.memo(EventDashboard);
